// เฝ้าที่นั่งของเครื่องนี้ — ใช้ร่วมกันระหว่าง plugin seatMain / seatSecond
//
//   boot     : ตรวจกับ RTDB ครั้งแรก
//                เป็นของเรา → ใช้ต่อ, ว่าง → จองเงียบ ๆ,
//                เป็นของคนอื่น / เต็ม → หน้า ask-*-override (โหมดยังไม่ถูกล้าง ให้ผู้ใช้เลือก)
//   ตอนรัน   : listener — เคยถือแล้วโดนแย่ง / เตะ → ล้างโหมด แล้วหน้า *-is-taken
//              พร้อม Computer ID ของคนที่เข้ามา (อ่านจาก kicks/{me})
//   heartbeat: ต่ออายุ lastSeen ทุก 60 วินาที เฉพาะตอนที่ยังถือที่นั่งอยู่
//
// ไป static ด้วย location.replace เสมอ (ไม่ใช่ router.push) เพื่อฆ่า listener / คิวพิมพ์ /
// watchHandshake ของโหมดเดิมให้หมดทันที
//
// ออฟไลน์ / error = ปล่อยใช้ไปก่อน (ร้านต้องขายได้) แล้วให้ listener ตรวจซ้ำตอนต่อติด

import type { Unsubscribe } from "firebase/database";
import { useRunningMode } from "../../core/composables/useRunningMode";
import { readComputerIdentity } from "../../core/composables/useComputerIdentity";
import { getResolvedStoreId } from "../../core/composables/useStoreIdentity";
import {
  SEAT_HEARTBEAT_MS,
  SEAT_MODE_MAIN,
  checkHolding,
  readKick,
  summarizeSeats,
  type ClaimDecision,
  type SeatMode,
} from "../utils/seatRules";
import {
  SEAT_CONFLICT_KEY,
  SEAT_LOST_KEY,
  SEAT_PAGES,
  writeJson,
  type SeatConflict,
  type SeatLost,
} from "../utils/seatStorage";
import {
  CONNECT_WAIT_MS,
  claimSeat,
  readSeats,
  readStoreName,
  releaseSeats,
  serverNow,
  touchSeat,
  waitForConnected,
  watchSeats,
} from "./seatRtdb";

export interface SeatContext {
  storeId: string;
  mode: SeatMode;
  /** Computer ID ของเครื่องนี้ (ต่อร้าน) */
  me: string;
  name: string;
  /** จำนวน Second ที่ร้านมีสิทธิ์ — null = ยังไม่รู้ (settings ยังไม่มา) ห้ามจอง Second */
  getQuota: () => number | null;
}

// หนึ่งหน้า (หนึ่งแท็บ) เฝ้าได้ชุดเดียว
let unsubscribe: Unsubscribe | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let stopped = false;

/** Computer ID + ชื่อเครื่องของร้านนี้ (useComputerIdentity) */
export function getSeatIdentity(storeId: string): { me: string; name: string } | null {
  const identity = readComputerIdentity(storeId);
  if (!identity?.id) return null;
  return { me: identity.id, name: identity.name?.trim() || identity.id };
}

/**
 * ตรวจที่นั่งตอน boot แล้วเฝ้าต่อ
 * @returns "redirected" = กำลังไปหน้า static ผู้เรียกต้องหยุด (อย่าให้แอปทำงานต่อ)
 */
export async function guardSeat(ctx: SeatContext): Promise<"ok" | "redirected"> {
  if (!(await waitForConnected(CONNECT_WAIT_MS))) {
    console.warn("[seat] ออฟไลน์ตอน boot ปล่อยใช้ไปก่อน จะตรวจอีกครั้งตอนต่อเน็ตได้");
    startWatcher(ctx, false);
    return "ok";
  }

  let seats: any;
  try {
    seats = await readSeats(ctx.storeId);
  } catch (error) {
    console.warn("[seat] อ่านที่นั่งไม่ได้ ปล่อยใช้ไปก่อน", error);
    startWatcher(ctx, false);
    return "ok";
  }

  const status = checkHolding(seats, ctx.mode, ctx.me);
  if (status === "held") {
    startWatcher(ctx, true);
    return "ok";
  }
  if (status === "lost") {
    await goConflict(ctx, conflictFromSeats(ctx, seats));
    return "redirected";
  }

  // missing — ว่าง (Main) / ยังไม่มีชื่อเรา (Second) → ลองจองเงียบ ๆ
  const outcome = await tryClaim(ctx);
  if (!outcome || outcome.result === "ok") {
    startWatcher(ctx, outcome?.result === "ok");
    return "ok";
  }
  await goConflict(ctx, outcome);
  return "redirected";
}

export function stopSeatGuard(): void {
  stopped = true;
  if (unsubscribe) unsubscribe();
  unsubscribe = null;
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  heartbeatTimer = null;
}

/** logout — หยุดเฝ้าก่อน (ไม่งั้น listener เห็นที่ว่างแล้วจองกลับ) แล้วคืนที่นั่ง */
export async function releaseMySeat(): Promise<void> {
  stopSeatGuard();
  const storeId = getResolvedStoreId();
  const identity = storeId ? getSeatIdentity(storeId) : null;
  if (!storeId || !identity) return;
  await releaseSeats(storeId, identity.me);
}

/** ค้างไว้รอเปลี่ยนหน้า — กัน bootstrap / งานของโหมดเดิมเริ่มทำงานระหว่างนั้น */
export function holdUntilNavigation(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 10000));
}

// ---------------------------------------------------------------------------

function startWatcher(ctx: SeatContext, confirmed: boolean) {
  stopSeatGuard();
  stopped = false;

  // everHeld = เคยยืนยันว่าถือที่นั่งในรอบนี้ — แยก "โดนแย่ง" (is-taken) ออกจาก
  // "boot มาเจอว่าไม่ใช่ของเรา" (ask-override)
  let everHeld = confirmed;
  let holding = false;
  let claiming = false;

  unsubscribe = watchSeats(ctx.storeId, async (value) => {
    if (stopped) return;

    const status = checkHolding(value, ctx.mode, ctx.me);
    if (status === "held") {
      everHeld = true;
      // เพิ่งได้ที่นั่ง — ต่ออายุทันที lastSeen เดิมอาจเก่าเป็นวัน
      if (!holding) {
        holding = true;
        void touchSeat(ctx.storeId, ctx.mode, ctx.me);
      }
      return;
    }
    holding = false;

    // Main เป็นของคนอื่น / Second ที่เคยถือแล้วชื่อหาย = โดนแย่ง / เตะ
    if (status === "lost" || (ctx.mode !== SEAT_MODE_MAIN && everHeld)) {
      if (everHeld) void goLost(ctx, value);
      else void goConflict(ctx, conflictFromSeats(ctx, value));
      return;
    }

    // missing ที่เหลือ: Main ว่าง / Second ที่ยังไม่เคยได้ที่ (boot ตอนออฟไลน์) → ลองจอง
    if (claiming) return;
    claiming = true;
    const outcome = await tryClaim(ctx);
    claiming = false;
    if (stopped || !outcome || outcome.result === "ok") return;
    if (everHeld) void goLost(ctx, value);
    else void goConflict(ctx, outcome);
  });

  // เขียนเฉพาะตอนที่ snapshot ล่าสุดยังเป็นของเรา — ถ้าโดนลบไปแล้ว update จะสร้าง
  // แถวผีขึ้นมาใหม่ (seatRules ไม่นับแถวที่ไม่มี name อยู่แล้ว แต่ไม่เขียนเลยดีกว่า)
  heartbeatTimer = setInterval(() => {
    if (!stopped && holding) void touchSeat(ctx.storeId, ctx.mode, ctx.me);
  }, SEAT_HEARTBEAT_MS);
}

async function tryClaim(ctx: SeatContext): Promise<ClaimDecision | null> {
  const quota = ctx.getQuota();
  if (ctx.mode !== SEAT_MODE_MAIN && quota == null) return null;
  try {
    return await claimSeat(ctx.storeId, {
      mode: ctx.mode,
      me: ctx.me,
      name: ctx.name,
      quota: quota ?? 0,
    });
  } catch (error) {
    console.warn("[seat] จองที่นั่งไม่สำเร็จ ปล่อยใช้ไปก่อน", error);
    return null;
  }
}

function conflictFromSeats(ctx: SeatContext, seats: any): Pick<ClaimDecision, "result" | "holders" | "used" | "quota"> {
  const summary = summarizeSeats(seats, serverNow());
  if (ctx.mode === SEAT_MODE_MAIN) {
    return {
      result: "main_taken",
      holders: summary.main ? [summary.main] : [],
      used: summary.secondUsed,
      quota: ctx.getQuota() ?? 0,
    };
  }
  return {
    result: "second_full",
    holders: summary.seconds,
    used: summary.secondUsed,
    quota: ctx.getQuota() ?? 0,
  };
}

/** boot มาเจอว่าที่นั่งไม่ใช่ของเรา → หน้าถามว่าจะแย่งหรือเปลี่ยนโหมด (ยังไม่ล้างโหมด) */
async function goConflict(
  ctx: SeatContext,
  outcome: Pick<ClaimDecision, "result" | "holders" | "used" | "quota">
) {
  stopSeatGuard();
  const conflict: SeatConflict = {
    mode: ctx.mode,
    reason: outcome.result === "ok" ? "second_full" : outcome.result,
    holders: outcome.holders,
    used: outcome.used,
    quota: outcome.quota,
    storeId: ctx.storeId,
    storeName: await readStoreName(ctx.storeId),
    computerId: ctx.me,
    at: new Date().toISOString(),
  };
  writeJson(SEAT_CONFLICT_KEY, conflict);
  window.location.replace(ctx.mode === SEAT_MODE_MAIN ? SEAT_PAGES.askMain : SEAT_PAGES.askSecond);
}

/** ใช้อยู่แล้วโดนแย่ง / เตะ → ล้างโหมด แล้วหน้าแจ้งพร้อม Computer ID คนที่เข้ามา */
async function goLost(ctx: SeatContext, seats: any) {
  stopSeatGuard();
  const summary = summarizeSeats(seats, serverNow());
  const lost: SeatLost = {
    mode: ctx.mode,
    by: readKick(seats, ctx.me),
    holders: ctx.mode === SEAT_MODE_MAIN ? (summary.main ? [summary.main] : []) : summary.seconds,
    storeId: ctx.storeId,
    storeName: await readStoreName(ctx.storeId),
    computerId: ctx.me,
    at: new Date().toISOString(),
  };
  writeJson(SEAT_LOST_KEY, lost);
  try {
    useRunningMode().clear();
  } catch (error) {
    console.warn("[seat] ล้างโหมดไม่สำเร็จ", error);
  }
  window.location.replace(ctx.mode === SEAT_MODE_MAIN ? SEAT_PAGES.mainTaken : SEAT_PAGES.secondTaken);
}
